import consts from "./consts.js";

/**
 * 获取用户信息缓存
 * 从 localStorage 中读取 KEY_INFO 对应的数据并解析为对象
 * @returns {Object|null} 用户信息对象，失败或不存在时返回 null
 */
export function getUserInfo() {
  try {
    const raw = localStorage.getItem(consts.KEY_INFO);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error("getUserInfo 解析失败:", e);
    return null;
  }
}

/**
 * 获取 Token 缓存
 * @returns {string|null}
 */
export function getToken() {
  return localStorage.getItem(consts.KEY_TOKEN);
}

/**
 * 通用获取缓存方法
 * @param {string} key consts 中定义的键名
 * @returns {Object|string|null}
 */
export function getCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch (e) {
    return null;
  }
}

/**
 * 更新用户信息缓存的指定字段
 * @param {Object} fields 要更新的字段对象
 */
export function updateUserInfo(fields) {
  try {
    const raw = localStorage.getItem(consts.KEY_INFO);
    let info = {};
    if (raw) {
      info = JSON.parse(raw);
    }
    const merged = deepMerge(info, fields);
    localStorage.setItem(consts.KEY_INFO, JSON.stringify(merged));
    return merged;
  } catch (e) {
    console.error("updateUserInfo 更新失败:", e);
    return null;
  }
}

/**
 * 通用更新缓存方法
 * @param {string} key consts 中定义的键名
 * @param {Object} fields 要更新的字段
 */
export function updateCache(key, fields) {
  try {
    const raw = localStorage.getItem(key);
    let data = {};
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = {};
      }
    }
    const merged = deepMerge(data, fields);
    localStorage.setItem(key, JSON.stringify(merged));
    return merged;
  } catch (e) {
    return null;
  }
}

/**
 * 深合并工具函数
 */
function deepMerge(target, source) {
  const result = { ...target };
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (
        typeof source[key] === "object" &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        typeof result[key] === "object" &&
        result[key] !== null &&
        !Array.isArray(result[key])
      ) {
        result[key] = deepMerge(result[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  return result;
}

/**
 * 转义 HTML 特殊字符，避免纯文本内容被当成标签解析
 * @param {string} text 原始文本
 * @returns {string}
 */
function escapeHtmlText(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * 过滤富文本中的脚本、内嵌页面与内联事件，只保留展示型 HTML
 * @param {string} html 富文本内容
 * @returns {string}
 */
function sanitizeRichHtml(html) {
  return String(html)
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta)\b[\s\S]*?<\s*\/\s*\1\s*>/gi,
      "",
    )
    .replace(/<\s*(script|iframe|object|embed|link|meta)\b[^>]*\/?>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\b(href|src)\s*=\s*(["'])\s*javascript:[\s\S]*?\2/gi, '$1="#"');
}

/**
 * 站内消息正文统一按 HTML 渲染
 * 后台富文本编辑器保存的是 HTML；历史纯文本内容转义后按段落还原，
 * 避免直接交给 v-html 后多段文字被合并成一行。
 * @param {string} content 消息正文
 * @returns {string} 可直接用于 v-html 的 HTML
 */
export function noticeHtml(content) {
  const raw = content == null ? "" : String(content).trim();
  if (!raw) return "";
  if (/<[a-z][\s\S]*>/i.test(raw)) return sanitizeRichHtml(raw);
  return raw
    .split(/\r?\n\s*\r?\n/)
    .map(
      (paragraph) =>
        `<p>${escapeHtmlText(paragraph).replace(/\r?\n/g, "<br />")}</p>`,
    )
    .join("");
}

/**
 * 富文本正文转纯文本（列表预览、摘要用）
 * @param {string} content 消息正文
 * @returns {string}
 */
export function noticeText(content) {
  const raw = content == null ? "" : String(content);
  if (!raw) return "";
  return raw
    .replace(/<\s*(script|style)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}
